/**
 * =====================================================================
 * IMPORTANT — READ BEFORE MODIFYING ANY LIST ENDPOINT IN THIS FILE:
 * =====================================================================
 *
 * TNN's Django backend paginates ALL list endpoints by default:
 *
 *   API returns: { "count": N, "results": [...actual data...] }
 *   baseApi unwraps to: { "count": N, "results": [...] }  (NOT an array)
 *
 * Every ``build.query<Type[], ...>`` endpoint MUST have:
 *
 *   transformResponse: (r: { results?: Type[] }) =>
 *     Array.isArray(r?.results) ? r.results : [],
 *
 * Without this, ``.map()`` on the paginated object either crashes
 * ("TypeError: x.map is not a function") or silently shows nothing.
 * This was the #1 bug class in the v1.6 and v4.0.1 QA reports.
 * =====================================================================
 */
/**
 * Accounting API — Cycle 1 (ledger) + Cycle 2 (contributions).
 *
 * Mirrors backend/accounting/urls.py, mounted at /api/v1/accounting/.
 * All endpoints tenant-scoped; finance writes require Owner/Accountant
 * server-side.
 */
import { baseApi } from "@/app/store/api/baseApi";

import type { Paginated } from "@/shared/api/types";
import type { MetricDrilldownData } from "@/shared/ui/MetricDrilldown";

const recentSubmissionsTag = { type: "Expense" as const, id: "RECENT_SUBMISSIONS" };

const postingRuleQueueInvalidationTags = [
  { type: "PostingRule" as const, id: "LIST" },
  { type: "ReviewQueue" as const, id: "LIST" },
  { type: "JournalApprovals" as const, id: "LIST" },
  { type: "JournalApprovals" as const, id: "SUMMARY" },
  { type: "AccountingDashboard" as const, id: "SUMMARY" },
];

import type {
  Account,
  AccountingAuditTrail,
  ActivitiesReport,
  Contribution,
  ContributionQueue,
  ContributionStatement,
  FinancialPositionReport,
  FiscalYear,
  FiscalYearCloseResult,
  Fund,
  FundraisingCampaign,
  FundBalancesReport,
  Journal,
  Pledge,
  QBOStatus,
  QBOSyncResult,
  SoftCredit,
  TopGiversResponse,
} from "../model/types";

/** Unwrap paginated `{ results }` or a plain array (DummyPagination endpoints). */
function unwrapList<T>(response: { results?: T[] } | T[] | null | undefined): T[] {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.results)) return response.results;
  return [];
}

function paginatedTransform<T>(response: { results?: T[]; count?: number }): {
  count: number;
  results: T[];
} {
  const results = unwrapList(response);
  return { count: response?.count ?? results.length, results };
}

/** Page size for dropdowns / maps — explicit `list_all: true`, not for paged tables. */
export const ACCOUNTING_FULL_LIST_PAGE_SIZE = 500;

const MANAGEMENT_DEFAULT_PAGE_SIZE = 10;

function accountingListQueryParams(
  params:
    | { page?: number; page_size?: number; list_all?: boolean; pledge?: number }
    | void,
  extra?: Record<string, string | number | boolean>,
): Record<string, string | number | boolean> {
  const p = params ?? {};
  const wantsAll =
    p.list_all === true || (p.page == null && p.page_size == null && p.pledge == null);
  if (wantsAll) {
    return { page: 1, page_size: ACCOUNTING_FULL_LIST_PAGE_SIZE, ...extra };
  }
  return {
    page: p.page ?? 1,
    page_size: p.page_size ?? MANAGEMENT_DEFAULT_PAGE_SIZE,
    ...(p.pledge != null ? { pledge: p.pledge } : {}),
    ...extra,
  };
}

/** RTK Query endpoints for the accounting domain (funds, accounts, journals, contributions, reports). */
export const accountingApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // --- Funds ---
    listFunds: build.query<
      { count: number; results: Fund[] },
      {
        include_archived?: boolean;
        is_active?: boolean;
        page?: number;
        page_size?: number;
        list_all?: boolean;
      } | void
    >({
      query: (params) => {
        const search = new URLSearchParams();
        const qp = accountingListQueryParams(params, {
          ...(params?.include_archived ? { include_archived: "true" } : {}),
          ...(params?.is_active === false ? { is_active: "false" } : {}),
          ...(params?.is_active === true ? { is_active: "true" } : {}),
        });
        for (const [key, value] of Object.entries(qp)) {
          search.set(key, String(value));
        }
        const qs = search.toString();
        return { url: `/accounting/funds/?${qs}` };
      },
      transformResponse: (r: { results?: Fund[]; count?: number }) => paginatedTransform(r),
      providesTags: [{ type: "Fund", id: "LIST" }],
    }),
    getFund: build.query<Fund, number>({
      query: (id) => `/accounting/funds/${id}/`,
      providesTags: (_r, _e, id) => [{ type: "Fund", id }],
    }),
    createFund: build.mutation<Fund, Partial<Fund>>({
      query: (body) => ({ url: "/accounting/funds/", method: "POST", body }),
      invalidatesTags: [{ type: "Fund", id: "LIST" }],
    }),
    updateFund: build.mutation<Fund, { id: number } & Partial<Fund>>({
      query: ({ id, ...body }) => ({
        url: `/accounting/funds/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Fund", id: "LIST" },
        { type: "Fund", id },
      ],
    }),
    changeFundRestriction: build.mutation<
      Fund,
      { id: number; restriction: Fund["restriction"]; reason: string }
    >({
      query: ({ id, restriction, reason }) => ({
        url: `/accounting/funds/${id}/change-restriction/`,
        method: "POST",
        body: { restriction, reason },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Fund", id: "LIST" },
        { type: "Fund", id },
      ],
      transformResponse: (r: { data?: Fund }) => r.data as Fund,
    }),
    deactivateFund: build.mutation<Fund, { id: number; effective_date: string; reason: string }>({
      query: ({ id, effective_date, reason }) => ({
        url: `/accounting/funds/${id}/deactivate/`,
        method: "POST",
        body: { effective_date, reason },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Fund", id: "LIST" },
        { type: "Fund", id },
      ],
      transformResponse: (r: { data?: Fund }) => r.data as Fund,
    }),
    reactivateFund: build.mutation<Fund, number>({
      query: (id) => ({
        url: `/accounting/funds/${id}/reactivate/`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "Fund", id: "LIST" },
        { type: "Fund", id },
      ],
      transformResponse: (r: { data?: Fund }) => r.data as Fund,
    }),

    listCampaigns: build.query<FundraisingCampaign[], { include_archived?: boolean } | void>({
      query: (params) => {
        const search = new URLSearchParams();
        if (params?.include_archived) {
          search.set("include_archived", "true");
        }
        const qs = search.toString();
        return { url: `/accounting/campaigns/${qs ? `?${qs}` : ""}` };
      },
      transformResponse: (r: { results?: FundraisingCampaign[] }) => unwrapList(r),
      providesTags: [{ type: "Campaign", id: "LIST" }],
    }),
    listCampaignsPage: build.query<
      Paginated<FundraisingCampaign>,
      { page?: number; page_size?: number; search?: string; include_archived?: boolean }
    >({
      query: ({ page, page_size, search, include_archived }) => ({
        url: "/accounting/campaigns/",
        params: {
          ...(page ? { page } : {}),
          ...(page_size ? { page_size } : {}),
          ...(search ? { search } : {}),
          ...(include_archived ? { include_archived: "true" } : {}),
        },
      }),
      providesTags: [{ type: "Campaign", id: "LIST" }],
    }),
    getCampaign: build.query<FundraisingCampaign, number>({
      query: (id) => `/accounting/campaigns/${id}/`,
      providesTags: (_r, _e, id) => [{ type: "Campaign", id }],
    }),
    createCampaign: build.mutation<FundraisingCampaign, Partial<FundraisingCampaign>>({
      query: (body) => ({ url: "/accounting/campaigns/", method: "POST", body }),
      invalidatesTags: [{ type: "Campaign", id: "LIST" }],
    }),
    updateCampaign: build.mutation<
      FundraisingCampaign,
      { id: number } & Partial<FundraisingCampaign>
    >({
      query: ({ id, ...body }) => ({
        url: `/accounting/campaigns/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "Campaign", id: "LIST" }],
    }),

    // --- Chart of accounts ---
    listAccounts: build.query<
      Account[],
      {
        source?: "all" | "qbo" | "seed";
        payment_eligible?: boolean;
        transfer_eligible?: boolean;
      } | void
    >({
      query: (params) => {
        const search = new URLSearchParams();
        if (params?.source && params.source !== "all") {
          search.set("source", params.source);
        }
        if (params?.payment_eligible) {
          search.set("payment_eligible", "true");
        }
        if (params?.transfer_eligible) {
          search.set("transfer_eligible", "true");
        }
        const qs = search.toString();
        return { url: `/accounting/accounts/${qs ? `?${qs}` : ""}` };
      },
      transformResponse: (r: { results?: Account[] } | Account[]) => unwrapList(r),
      providesTags: [{ type: "Account", id: "LIST" }],
    }),
    createAccount: build.mutation<Account, Partial<Account>>({
      query: (body) => ({ url: "/accounting/accounts/", method: "POST", body }),
      invalidatesTags: [{ type: "Account", id: "LIST" }],
    }),
    updateAccount: build.mutation<Account, { id: number } & Partial<Account>>({
      query: ({ id, ...body }) => ({
        url: `/accounting/accounts/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "Account", id: "LIST" }],
    }),

    // --- Journals ---
    listJournals: build.query<
      { count: number; results: Journal[] },
      { page?: number; page_size?: number } | void
    >({
      query: (params) => {
        const { page, page_size, ...rest } = params ?? {};
        return {
          url: "/accounting/journals/",
          params: {
            ...rest,
            ...(page ? { page } : {}),
            ...(page_size ? { page_size } : {}),
          },
        };
      },
      transformResponse: (r: { results?: Journal[]; count?: number }) => ({
        count: r?.count ?? 0,
        results: Array.isArray(r?.results) ? r.results : [],
      }),
      providesTags: [{ type: "Journal", id: "LIST" }],
    }),
    getJournal: build.query<Journal, number>({
      query: (id) => ({ url: `/accounting/journals/${id}/` }),
      providesTags: (_r, _e, id) => [{ type: "Journal", id }],
    }),
    createJournal: build.mutation<Journal, Partial<Journal>>({
      query: (body) => ({ url: "/accounting/journals/", method: "POST", body }),
      invalidatesTags: [{ type: "Journal", id: "LIST" }],
    }),
    updateJournal: build.mutation<Journal, { id: number } & Partial<Journal>>({
      query: ({ id, ...body }) => ({
        url: `/accounting/journals/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Journal", id },
        { type: "Journal", id: "LIST" },
      ],
    }),
    postJournal: build.mutation<Journal, number>({
      query: (id) => ({
        url: `/accounting/journals/${id}/post_entry/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "Journal", id: "LIST" },
        { type: "Fund", id: "LIST" },
        { type: "ReviewQueue", id: "LIST" },
        { type: "JournalApprovals", id: "LIST" },
        { type: "JournalApprovals", id: "SUMMARY" },
        { type: "AccountingDashboard", id: "SUMMARY" },
      ],
    }),
    voidJournal: build.mutation<Journal, number>({
      query: (id) => ({
        url: `/accounting/journals/${id}/void/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "Journal", id: "LIST" },
        { type: "Fund", id: "LIST" },
      ],
    }),
    reverseJournal: build.mutation<Journal, number>({
      query: (id) => ({
        url: `/accounting/journals/${id}/reverse/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "Journal", id: "LIST" },
        { type: "Fund", id: "LIST" },
      ],
    }),

    // --- Fiscal years / periods ---
    listFiscalYears: build.query<FiscalYear[], void>({
      query: () => ({ url: "/accounting/fiscal-years/" }),
      transformResponse: (r: { results?: FiscalYear[] }) =>
        Array.isArray(r?.results) ? r.results : [],
      providesTags: [{ type: "FiscalYear", id: "LIST" }],
    }),

    contributionQueue: build.query<ContributionQueue, void>({
      query: () => "/accounting/contributions/queue/",
      providesTags: [{ type: "Contribution", id: "QUEUE" }],
    }),

    alignContribution: build.mutation<Contribution, number>({
      query: (id) => ({
        url: `/accounting/contributions/${id}/align/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "Contribution", id: "QUEUE" },
        { type: "Contribution", id: "LIST" },
      ],
    }),

    closeFiscalYear: build.mutation<FiscalYearCloseResult, number>({
      query: (id) => ({
        url: `/accounting/fiscal-years/${id}/close/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "FiscalYear", id: "LIST" },
        { type: "Journal", id: "LIST" },
        { type: "Fund", id: "LIST" },
      ],
    }),

    // --- Contributions (Cycle 2) ---
    listContributions: build.query<
      { count: number; results: Contribution[] },
      { page?: number; page_size?: number; pledge?: number; list_all?: boolean } | void
    >({
      query: (params) => ({
        url: "/accounting/contributions/",
        params: accountingListQueryParams(params),
      }),
      transformResponse: (r: { results?: Contribution[]; count?: number }) =>
        paginatedTransform(r),
      providesTags: [{ type: "Contribution", id: "LIST" }],
    }),
    createContribution: build.mutation<Contribution, Partial<Contribution>>({
      query: (body) => ({
        url: "/accounting/contributions/",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Contribution", id: "LIST" },
        { type: "Contribution", id: "QUEUE" },
        { type: "Donor", id: "LIST" },
        { type: "Donor", id: "RECENT" },
        recentSubmissionsTag,
      ],
    }),
    postContribution: build.mutation<Contribution, number>({
      query: (id) => ({
        url: `/accounting/contributions/${id}/post/`,
        method: "POST",
      }),
      invalidatesTags: (result) => [
        { type: "Contribution", id: "LIST" },
        { type: "Fund", id: "LIST" },
        { type: "Journal", id: "LIST" },
        { type: "Interaction", id: "SUMMARY" },
        { type: "Person", id: "LIST" },
        ...(result?.attributed_person
          ? [{ type: "Person" as const, id: result.attributed_person }]
          : []),
      ],
    }),
    voidContribution: build.mutation<Contribution, number>({
      query: (id) => ({
        url: `/accounting/contributions/${id}/void/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "Contribution", id: "LIST" },
        { type: "Fund", id: "LIST" },
      ],
    }),

    // --- Soft credits (Cycle 2) ---
    createSoftCredit: build.mutation<SoftCredit, Partial<SoftCredit>>({
      query: (body) => ({
        url: "/accounting/soft-credits/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Contribution", id: "LIST" }],
    }),

    // --- Pledges (Cycle 2) ---
    listPledges: build.query<
      { count: number; results: Pledge[] },
      { page?: number; page_size?: number; list_all?: boolean } | void
    >({
      query: (params) => ({
        url: "/accounting/pledges/",
        params: accountingListQueryParams(params),
      }),
      transformResponse: (r: { results?: Pledge[]; count?: number }) => paginatedTransform(r),
      providesTags: [{ type: "Pledge", id: "LIST" }],
    }),
    createPledge: build.mutation<Pledge, Partial<Pledge>>({
      query: (body) => ({ url: "/accounting/pledges/", method: "POST", body }),
      invalidatesTags: [
        { type: "Pledge", id: "LIST" },
        { type: "Donor", id: "LIST" },
      ],
    }),
    recordPledgeReceivable: build.mutation<{ pledge: Pledge; proposal_id: number }, number>({
      query: (id) => ({
        url: `/accounting/pledges/${id}/record-receivable/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "Pledge", id: "LIST" },
        { type: "ReviewQueue", id: "LIST" },
      ],
    }),
    writeOffPledge: build.mutation<Pledge, { id: number; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/accounting/pledges/${id}/write-off/`,
        method: "POST",
        body: reason ? { reason } : {},
      }),
      invalidatesTags: [{ type: "Pledge", id: "LIST" }],
    }),
    cancelPledge: build.mutation<Pledge, { id: number; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/accounting/pledges/${id}/cancel/`,
        method: "POST",
        body: reason ? { reason } : {},
      }),
      invalidatesTags: [{ type: "Pledge", id: "LIST" }],
    }),
    uploadPledgeAttachment: build.mutation<Pledge, { id: number; file: File }>({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/accounting/pledges/${id}/upload-attachment/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [{ type: "Pledge", id: "LIST" }],
    }),

    getAccountingAudit: build.query<
      AccountingAuditTrail,
      { resource: string; id: number; basePath?: string }
    >({
      query: ({ resource, id, basePath = "/accounting" }) => ({
        url: `${basePath}/${resource}/${id}/audit/`,
      }),
    }),

    // --- QuickBooks Online (Cycle 3) ---
    qboStatus: build.query<QBOStatus, void>({
      query: () => ({ url: "/accounting/qbo/status/" }),
      providesTags: [{ type: "QBO", id: "STATUS" }],
    }),
    qboConnect: build.mutation<{ authorization_url: string }, void>({
      query: () => ({ url: "/accounting/qbo/connect/", method: "POST" }),
    }),
    qboDisconnect: build.mutation<{ status: string }, void>({
      query: () => ({ url: "/accounting/qbo/disconnect/", method: "POST" }),
      invalidatesTags: [{ type: "QBO", id: "STATUS" }],
    }),
    qboSyncAccounts: build.mutation<QBOSyncResult, void>({
      query: () => ({
        url: "/accounting/qbo/sync-accounts/",
        method: "POST",
      }),
      invalidatesTags: [
        { type: "QBO", id: "STATUS" },
        { type: "QBO", id: "SYNC_LIST" },
        { type: "Account", id: "LIST" },
      ],
    }),
    qboPullBankTransactions: build.mutation<
      {
        ok: boolean;
        created: number;
        updated: number;
        skipped: number;
        pending_in_review: number;
        summary: string;
      },
      void
    >({
      query: () => ({
        url: "/accounting/qbo/pull-bank-transactions/",
        method: "POST",
      }),
      invalidatesTags: [
        { type: "QBO", id: "STATUS" },
        { type: "QBO", id: "SYNC_LIST" },
        { type: "ReviewQueue", id: "LIST" },
      ],
    }),
    qboPendingJournals: build.query<{ ids: number[]; count: number }, void>({
      query: () => ({ url: "/accounting/qbo-sync/pending-journals/" }),
      providesTags: [{ type: "QBO", id: "PENDING_JOURNALS" }],
    }),
    qboPushJournal: build.mutation<QBOSyncResult, number>({
      query: (journal) => ({
        url: "/accounting/qbo/push-journal/",
        method: "POST",
        body: { journal },
      }),
      invalidatesTags: [
        { type: "QBO", id: "STATUS" },
        { type: "QBO", id: "SYNC_LIST" },
        { type: "QBO", id: "PENDING_JOURNALS" },
        { type: "Journal", id: "LIST" },
      ],
    }),

    // --- Financial reports (Cycle 4) ---
    financialPosition: build.query<FinancialPositionReport, { as_of?: string } | void>({
      query: (args) => ({
        url: "/accounting/reports/financial-position/",
        params: args && args.as_of ? { as_of: args.as_of } : undefined,
      }),
    }),
    activitiesReport: build.query<
      ActivitiesReport,
      { date_from?: string; date_to?: string } | void
    >({
      query: (args) => ({
        url: "/accounting/reports/activities/",
        params: args || undefined,
      }),
    }),
    fundBalancesReport: build.query<FundBalancesReport, { as_of?: string } | void>({
      query: (args) => ({
        url: "/accounting/reports/fund-balances/",
        params: args && args.as_of ? { as_of: args.as_of } : undefined,
      }),
    }),
    /**
     * Drilldown for the dashboard's "Unrestricted fund balance" tile.
     * Returns the {summary, rows, notes, footer} popover contract.
     */
    fundBalanceBreakdown: build.query<MetricDrilldownData, void>({
      query: () => "/accounting/reports/fund-balance-breakdown/",
    }),

    getTopGivers: build.query<TopGiversResponse, number>({
      query: (year) => ({
        url: "/accounting/contributions/top-givers/",
        params: { year },
      }),
    }),

    getContributionStatement: build.query<
      ContributionStatement,
      { personId: number; year: number }
    >({
      query: ({ personId, year }) => ({
        url: "/accounting/contributions/statement/",
        params: { person_id: personId, year },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useListFundsQuery,
  useGetFundQuery,
  useCreateFundMutation,
  useUpdateFundMutation,
  useChangeFundRestrictionMutation,
  useDeactivateFundMutation,
  useReactivateFundMutation,
  useListCampaignsQuery,
  useLazyListCampaignsPageQuery,
  useGetCampaignQuery,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useListAccountsQuery,
  useCreateAccountMutation,
  useUpdateAccountMutation,
  useListJournalsQuery,
  useGetJournalQuery,
  useCreateJournalMutation,
  useUpdateJournalMutation,
  usePostJournalMutation,
  useVoidJournalMutation,
  useReverseJournalMutation,
  useListFiscalYearsQuery,
  useContributionQueueQuery,
  useAlignContributionMutation,
  useCloseFiscalYearMutation,
  useListContributionsQuery,
  useCreateContributionMutation,
  usePostContributionMutation,
  useVoidContributionMutation,
  useCreateSoftCreditMutation,
  useListPledgesQuery,
  useCreatePledgeMutation,
  useRecordPledgeReceivableMutation,
  useWriteOffPledgeMutation,
  useCancelPledgeMutation,
  useUploadPledgeAttachmentMutation,
  useGetAccountingAuditQuery,
  useQboStatusQuery,
  useQboConnectMutation,
  useQboDisconnectMutation,
  useQboSyncAccountsMutation,
  useQboPullBankTransactionsMutation,
  useQboPendingJournalsQuery,
  useQboPushJournalMutation,
  useFinancialPositionQuery,
  useActivitiesReportQuery,
  useFundBalancesReportQuery,
  useFundBalanceBreakdownQuery,
  useGetTopGiversQuery,
  useLazyGetContributionStatementQuery,
} = accountingApi;

const expenseInvalidationTags = [
  { type: "Expense" as const, id: "LIST" },
  recentSubmissionsTag,
  { type: "ReviewQueue" as const, id: "LIST" },
  { type: "JournalApprovals" as const, id: "LIST" },
  { type: "JournalApprovals" as const, id: "SUMMARY" },
  { type: "AccountingDashboard" as const, id: "SUMMARY" },
];

type CaseTimelineTag = { type: "CaseTimeline"; id: number };

/** Invalidate case timeline when an expense is tied to a service case. */
function caseTimelineTagsFromExpenseChange(result: unknown, arg: unknown): CaseTimelineTag[] {
  const tags: CaseTimelineTag[] = [];
  const push = (id: number) => {
    if (Number.isFinite(id) && id > 0 && !tags.some((t) => t.id === id)) {
      tags.push({ type: "CaseTimeline", id });
    }
  };
  const fromResult = (result as { service_case?: number } | undefined)?.service_case;
  if (fromResult) push(fromResult);

  if (arg instanceof FormData) {
    const raw = arg.get("service_case");
    if (raw != null && raw !== "") push(Number(raw));
  } else if (arg && typeof arg === "object" && "service_case" in arg) {
    push(Number((arg as Record<string, unknown>).service_case));
  }
  return tags;
}

function expenseInvalidatesWithCaseTimeline(result: unknown, _error: unknown, arg: unknown) {
  return [...expenseInvalidationTags, ...caseTimelineTagsFromExpenseChange(result, arg)];
}

/** Row shape from GET /accounting/expenses/ (list). */
export type ExpenseListItem = {
  id: number;
  business_purpose: string;
  amount: string;
  spent_on: string;
  status: string;
  status_display?: string;
  workflow_stage?: string;
  workflow_stage_display?: string;
  sync_retry_available?: boolean;
  expense_number?: string;
  vendor_display?: string;
  vendor_name?: string;
  submitted_by_name?: string;
};

export type ListExpensesParams = {
  status?: string;
  service_case?: number;
  page?: number;
  page_size?: number;
};

// Expenses
export const {
  useListExpensesQuery,
  useCreateExpenseMutation,
  useLazyCheckExpenseDuplicateQuery,
  useSubmitExpenseMutation,
  useOfficerApproveExpenseMutation,
  useListSubmissionCenterQuery,
  useListRecentSubmissionsQuery,
  useApproveSubmissionCenterMutation,
  useApproveExpenseMutation,
  useRejectExpenseMutation,
  useCodeExpenseMutation,
} = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    listExpenses: builder.query<Paginated<ExpenseListItem>, ListExpensesParams | void>({
      query: (params) => ({
        url: `/accounting/expenses/`,
        params: params ?? {},
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((e) => ({ type: "Expense" as const, id: e.id })),
              { type: "Expense", id: "LIST" },
            ]
          : [{ type: "Expense", id: "LIST" }],
    }),
    createExpense: builder.mutation({
      queryFn: async (body: Record<string, unknown> | FormData, _api, _extra, baseQuery) => {
        type LinePayload = {
          description: string;
          amount: string;
          quantity?: string;
          program?: number;
        };
        let lines: LinePayload[] = [];
        let expenseBody: Record<string, unknown> | FormData = body;
        if (body instanceof FormData) {
          const raw = body.get("lines");
          if (typeof raw === "string" && raw) {
            lines = JSON.parse(raw) as LinePayload[];
          }
          body.delete("lines");
          expenseBody = body;
        } else {
          const { lines: payloadLines, ...rest } = body;
          lines = (payloadLines as LinePayload[] | undefined) ?? [];
          expenseBody = rest;
        }
        const expenseResult = await baseQuery({
          url: `/accounting/expenses/`,
          method: "POST",
          body: expenseBody,
        });
        if (expenseResult.error) return { error: expenseResult.error };
        const expense = expenseResult.data as { id: number; service_case?: number };
        for (const line of lines) {
          const lineResult = await baseQuery({
            url: `/accounting/expense-lines/`,
            method: "POST",
            body: { ...line, expense: expense.id },
          });
          if (lineResult.error) return { error: lineResult.error };
        }
        return { data: expense };
      },
      invalidatesTags: expenseInvalidatesWithCaseTimeline,
    }),
    checkExpenseDuplicate: builder.query({
      query: (params: {
        amount: string;
        spent_on: string;
        expense_number?: string;
        vendor_name?: string;
        vendor?: number;
        exclude_id?: number;
      }) => ({
        url: `/accounting/expenses/check-duplicate/`,
        params,
      }),
    }),
    submitExpense: builder.mutation({
      query: (args: number | { id: number; force?: boolean }) => {
        const id = typeof args === "number" ? args : args.id;
        const force = typeof args === "object" ? args.force : false;
        return {
          url: `/accounting/expenses/${id}/submit/`,
          method: "POST",
          body: force ? { force: true } : {},
        };
      },
      invalidatesTags: expenseInvalidatesWithCaseTimeline,
    }),
    officerApproveExpense: builder.mutation({
      query: (id) => ({
        url: `/accounting/expenses/${id}/officer-approve/`,
        method: "POST",
      }),
      invalidatesTags: expenseInvalidatesWithCaseTimeline,
    }),
    listSubmissionCenter: builder.query({
      query: () => `/accounting/submission-center/`,
      transformResponse: (r: { results?: unknown[] } | unknown[]) =>
        Array.isArray(r) ? r : (r?.results ?? []),
      providesTags: [{ type: "Expense", id: "LIST" }],
    }),
    listRecentSubmissions: builder.query({
      query: (params?: {
        q?: string;
        kind?: string;
        status?: string;
        payment_type?: string;
        submitted_by?: string;
        page?: number;
        page_size?: number;
      }) => ({
        url: `/accounting/submission-center/recent/`,
        params,
      }),
      providesTags: [recentSubmissionsTag],
    }),
    approveSubmissionCenter: builder.mutation({
      query: (body: { kind: string; id: number }) => ({
        url: `/accounting/submission-center/approve/`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        ...expenseInvalidatesWithCaseTimeline(result, error, arg),
        { type: "Bill" as const, id: "LIST" },
        { type: "Grant" as const, id: "LIST" },
        { type: "Grant" as const, id: "BUDGET_LINES" },
        recentSubmissionsTag,
      ],
    }),
    approveExpense: builder.mutation({
      query: (id) => ({ url: `/accounting/expenses/${id}/approve/`, method: "POST" }),
      invalidatesTags: expenseInvalidatesWithCaseTimeline,
    }),
    rejectExpense: builder.mutation({
      query: ({ id, reason }) => ({
        url: `/accounting/expenses/${id}/reject/`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: expenseInvalidatesWithCaseTimeline,
    }),
    codeExpense: builder.mutation({
      query: ({
        id,
        ...body
      }: {
        id: number;
        grant?: number | null;
        fund?: number | null;
        program?: number | null;
        assistance_program?: number | null;
        expense_account?: number | null;
        payment_account?: number | null;
        payment_source?: string;
      }) => ({
        url: `/accounting/expenses/${id}/code/`,
        method: "POST",
        body,
      }),
      invalidatesTags: expenseInvalidatesWithCaseTimeline,
    }),
  }),
});

// Posting rules (QuickBooks-style bank categorization)
export const {
  useListPostingRulesQuery,
  useCreatePostingRuleMutation,
  useUpdatePostingRuleMutation,
  useDeletePostingRuleMutation,
  useReorderPostingRulesMutation,
  useReapplyPendingPostingRulesMutation,
  useAcceptSuggestionMutation,
  useRejectSuggestionMutation,
} = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    listPostingRules: builder.query<
      { count: number; results: unknown[] },
      { page?: number; page_size?: number } | void
    >({
      query: (params) => ({
        url: `/accounting/posting-rule-overrides/`,
        params: {
          ...(params?.page ? { page: params.page } : {}),
          ...(params?.page_size ? { page_size: params.page_size } : {}),
        },
      }),
      transformResponse: (r: unknown) => {
        if (Array.isArray(r)) {
          return { count: r.length, results: r };
        }
        const payload = r as { count?: number; results?: unknown[] };
        return {
          count: payload?.count ?? 0,
          results: Array.isArray(payload?.results) ? payload.results : [],
        };
      },
      providesTags: [{ type: "PostingRule", id: "LIST" }],
    }),
    createPostingRule: builder.mutation({
      query: (body) => ({
        url: `/accounting/posting-rule-overrides/`,
        method: "POST",
        body,
      }),
      invalidatesTags: postingRuleQueueInvalidationTags,
    }),
    updatePostingRule: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/accounting/posting-rule-overrides/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: postingRuleQueueInvalidationTags,
    }),
    deletePostingRule: builder.mutation({
      query: (id: number) => ({
        url: `/accounting/posting-rule-overrides/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "PostingRule", id: "LIST" }],
    }),
    reorderPostingRules: builder.mutation({
      query: (rules: { id: number; priority: number }[]) => ({
        url: `/accounting/posting-rule-overrides/reorder/`,
        method: "POST",
        body: rules,
      }),
      invalidatesTags: postingRuleQueueInvalidationTags,
    }),
    reapplyPendingPostingRules: builder.mutation<
      {
        expenses_routed: number;
        proposals_refreshed: number;
        expenses_recoded?: number;
        skipped: number;
        skipped_posted?: number;
        errors: number;
      },
      { recode_posted?: boolean } | void
    >({
      query: (body) => ({
        url: `/accounting/posting-rule-overrides/reapply-pending/`,
        method: "POST",
        body: body ?? {},
      }),
      invalidatesTags: postingRuleQueueInvalidationTags,
    }),
    acceptSuggestion: builder.mutation({
      query: ({ id, assignments }: { id: number; assignments?: unknown[] }) => ({
        url: `/accounting/reconciliation-items/${id}/accept-suggestion/`,
        method: "POST",
        body: assignments ? { assignments } : {},
      }),
      invalidatesTags: ["BankRec"],
    }),
    rejectSuggestion: builder.mutation({
      query: (id: number) => ({
        url: `/accounting/reconciliation-items/${id}/reject-suggestion/`,
        method: "POST",
      }),
      invalidatesTags: ["BankRec"],
    }),
  }),
});

// Legacy posting rules admin (templates — read-only)
export const {
  useListPostingRuleTemplatesQuery,
  useListConceptsQuery,
  useListAccountMappingsQuery,
  useCreateAccountMappingMutation,
  useListPostingRuleOverridesQuery,
  useCreatePostingRuleOverrideMutation,
  useUpdatePostingRuleOverrideMutation,
} = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    listPostingRuleTemplates: builder.query({
      query: () => `/accounting/posting-rules/`,
      transformResponse: (r: any) => r?.results ?? r,
      providesTags: [{ type: "PostingRule", id: "TEMPLATES" }],
    }),
    listConcepts: builder.query({
      query: () => `/accounting/concepts/`,
      transformResponse: (r: any) => r?.results ?? r,
    }),
    listAccountMappings: builder.query({
      query: () => `/accounting/account-mappings/`,
      transformResponse: (r: any) => r?.results ?? r,
    }),
    createAccountMapping: builder.mutation({
      query: (body) => ({
        url: `/accounting/account-mappings/`,
        method: "POST",
        body,
      }),
    }),
    listPostingRuleOverrides: builder.query({
      query: () => `/accounting/posting-rule-overrides/`,
      transformResponse: (r: any) => r?.results ?? r,
      providesTags: [{ type: "PostingRule", id: "LIST" }],
    }),
    createPostingRuleOverride: builder.mutation({
      query: (body) => ({
        url: `/accounting/posting-rule-overrides/`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "PostingRule", id: "LIST" },
        { type: "PostingRule", id: "TEMPLATES" },
      ],
    }),
    updatePostingRuleOverride: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/accounting/posting-rule-overrides/${id}/`,
        method: "PATCH",
        body,
      }),
    }),
  }),
});

// GL reports & budget/bank extensions
export const {
  useTransactionSearchQuery,
  useCashFlowReportQuery,
  useFunctionalExpensesReportQuery,
  useBudgetVsActualReportQuery,
  useCreateBudgetLineMutation,
  useListBudgetAdjustmentsQuery,
  useCreateBankRecMutation,
  useImportBankRecCsvMutation,
} = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    transactionSearch: builder.query({
      query: (params) => ({
        url: `/accounting/reports/transaction-search/`,
        params: {
          from: params?.date_from,
          to: params?.date_to,
          account_id: params?.account,
          fund_id: params?.fund,
          q: params?.q,
        },
      }),
    }),
    cashFlowReport: builder.query({
      query: (params: { from: string; to: string }) => ({
        url: `/accounting/reports/cash-flow/`,
        params: { from: params.from, to: params.to },
      }),
    }),
    functionalExpensesReport: builder.query({
      query: (params: { from: string; to: string }) => ({
        url: `/accounting/reports/functional-expenses/`,
        params: { from: params.from, to: params.to },
      }),
    }),
    budgetVsActualReport: builder.query({
      query: (params?: { budget_id?: number }) => ({
        url: `/accounting/reports/budget-vs-actual/`,
        params: params?.budget_id ? { budget_id: params.budget_id } : undefined,
      }),
    }),
    createBudgetLine: builder.mutation({
      query: (body) => ({
        url: `/accounting/budget-lines/`,
        method: "POST",
        body,
      }),
    }),
    listBudgetAdjustments: builder.query({
      query: (params?: { budget_line?: number }) => ({
        url: `/accounting/budget-adjustments/`,
        params,
      }),
      transformResponse: (r: any) => r?.results ?? r,
    }),
    createBankRec: builder.mutation({
      query: (body) => ({
        url: `/accounting/bank-reconciliations/`,
        method: "POST",
        body,
      }),
    }),
    importBankRecCsv: builder.mutation({
      query: ({ id, file }: { id: number; file: File }) => {
        const form = new FormData();
        form.append("file", file);
        return {
          url: `/accounting/bank-reconciliations/${id}/import-csv/`,
          method: "POST",
          body: form,
        };
      },
    }),
  }),
});

// Vendors
export const { useListVendorsQuery } = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    listVendors: builder.query({
      query: () => `/accounting/vendors/`,
      transformResponse: (r: any) => r?.results ?? r,
      providesTags: [{ type: "Vendor", id: "LIST" }],
    }),
  }),
});

// Bills (AP)
export const {
  useListBillsQuery,
  useCodeBillMutation,
  useApproveBillMutation,
  useRejectBillMutation,
  useOfficerApproveBillMutation,
  useBillAgingQuery,
} = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    listBills: builder.query({
      query: (params?: {
        status?: string;
        vendor?: number;
        date_from?: string;
        date_to?: string;
        page_size?: number;
      }) => ({ url: `/accounting/bills/`, params }),
      transformResponse: (r: any) => r?.results ?? r,
      providesTags: [{ type: "Bill", id: "LIST" }],
    }),
    codeBill: builder.mutation({
      query: ({
        id,
        lines,
        payment_account,
      }: {
        id: number;
        lines: {
          id: number;
          account: number;
          fund?: number | null;
          program?: number | null;
          assistance_program?: number | null;
        }[];
        payment_account?: number | null;
      }) => ({
        url: `/accounting/bills/${id}/code/`,
        method: "POST",
        body: {
          lines,
          ...(payment_account != null ? { payment_account } : {}),
        },
      }),
      invalidatesTags: [
        { type: "Bill", id: "LIST" },
        { type: "Bill", id: "AGING" },
        { type: "AccountingDashboard", id: "SUMMARY" },
        { type: "ReviewQueue", id: "LIST" },
      ],
    }),
    approveBill: builder.mutation({
      query: (id) => ({ url: `/accounting/bills/${id}/approve/`, method: "POST" }),
      invalidatesTags: [
        { type: "ReviewQueue", id: "LIST" },
        { type: "JournalApprovals", id: "LIST" },
        { type: "JournalApprovals", id: "SUMMARY" },
        { type: "Bill", id: "LIST" },
        { type: "Bill", id: "AGING" },
        { type: "AccountingDashboard", id: "SUMMARY" },
      ],
    }),
    rejectBill: builder.mutation({
      query: ({ id, reason }: { id: number; reason: string }) => ({
        url: `/accounting/bills/${id}/reject/`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: [
        { type: "ReviewQueue", id: "LIST" },
        { type: "Bill", id: "LIST" },
        { type: "Bill", id: "AGING" },
        { type: "AccountingDashboard", id: "SUMMARY" },
        recentSubmissionsTag,
      ],
    }),
    officerApproveBill: builder.mutation({
      query: (id) => ({
        url: `/accounting/bills/${id}/officer-approve/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "ReviewQueue", id: "LIST" },
        { type: "Bill", id: "LIST" },
        { type: "Bill", id: "AGING" },
        { type: "AccountingDashboard", id: "SUMMARY" },
      ],
    }),
    billAging: builder.query({
      query: () => `/accounting/bills/aging/`,
      providesTags: [{ type: "Bill", id: "AGING" }],
    }),
  }),
});

// Invoices (AR)
export const { useListInvoicesQuery, useInvoiceAgingQuery, useSendInvoiceMutation } =
  accountingApi.injectEndpoints({
    endpoints: (builder) => ({
      listInvoices: builder.query({
        query: (params?: { date_from?: string; date_to?: string; status?: string }) => ({
          url: `/accounting/invoices/`,
          params,
        }),
        transformResponse: (r: any) => r?.results ?? r,
      }),
      invoiceAging: builder.query({
        query: () => `/accounting/invoices/aging/`,
      }),
      sendInvoice: builder.mutation({
        query: (id) => ({ url: `/accounting/invoices/${id}/send/`, method: "POST" }),
      }),
    }),
  });

// Budgets
export const { useListBudgetsQuery, useBudgetVarianceQuery, useBudgetForecastQuery } =
  accountingApi.injectEndpoints({
    endpoints: (builder) => ({
      listBudgets: builder.query({
        query: () => `/accounting/budgets/`,
        transformResponse: (r: any) => r?.results ?? r,
      }),
      budgetVariance: builder.query({
        query: (id) => `/accounting/budgets/${id}/variance/`,
      }),
      budgetForecast: builder.query({
        query: (id) => `/accounting/budgets/${id}/forecast/`,
      }),
    }),
  });

// Posting Proposals
export const {
  useListPostingProposalsQuery,
  useSubmitProposalMutation,
  useApproveProposalMutation,
  useApproveAllProposalsMutation,
  useConfirmProposalMutation,
  useRejectProposalMutation,
  usePendingProposalsQuery,
  useGenerateMissingProposalsMutation,
} = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    listPostingProposals: builder.query({
      query: (params) => ({ url: `/accounting/posting-proposals/`, params }),
      transformResponse: (r: any) => r?.results ?? r,
    }),
    submitProposal: builder.mutation({
      query: ({ id, lines }: { id: number; lines?: Record<string, unknown>[] }) => ({
        url: `/accounting/posting-proposals/${id}/submit/`,
        method: "POST",
        body: lines ? { lines } : {},
      }),
      invalidatesTags: [
        { type: "ReviewQueue", id: "LIST" },
        { type: "JournalApprovals", id: "LIST" },
        { type: "JournalApprovals", id: "SUMMARY" },
        { type: "Journal", id: "LIST" },
        { type: "Expense", id: "LIST" },
        { type: "AccountingDashboard", id: "SUMMARY" },
      ],
    }),
    approveProposal: builder.mutation({
      query: ({ id, lines }: { id: number; lines?: Record<string, unknown>[] }) => ({
        url: `/accounting/posting-proposals/${id}/approve/`,
        method: "POST",
        body: lines ? { lines } : {},
      }),
      invalidatesTags: [
        { type: "ReviewQueue", id: "LIST" },
        { type: "JournalApprovals", id: "LIST" },
        { type: "JournalApprovals", id: "SUMMARY" },
        { type: "Journal", id: "LIST" },
        { type: "Expense", id: "LIST" },
        { type: "AccountingDashboard", id: "SUMMARY" },
      ],
    }),
    approveAllProposals: builder.mutation<
      { approved_count: number; approved: unknown[]; errors: unknown[] },
      void
    >({
      query: () => ({
        url: `/accounting/posting-proposals/approve-all/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "ReviewQueue", id: "LIST" },
        { type: "JournalApprovals", id: "LIST" },
        { type: "JournalApprovals", id: "SUMMARY" },
        { type: "Journal", id: "LIST" },
        { type: "Expense", id: "LIST" },
        { type: "AccountingDashboard", id: "SUMMARY" },
      ],
    }),
    confirmProposal: builder.mutation({
      query: ({ id, lines }: { id: number; lines?: Record<string, unknown>[] }) => ({
        url: `/accounting/posting-proposals/${id}/submit/`,
        method: "POST",
        body: lines ? { lines } : {},
      }),
      invalidatesTags: [
        { type: "ReviewQueue", id: "LIST" },
        { type: "JournalApprovals", id: "LIST" },
        { type: "JournalApprovals", id: "SUMMARY" },
        { type: "Journal", id: "LIST" },
        { type: "Expense", id: "LIST" },
        { type: "AccountingDashboard", id: "SUMMARY" },
      ],
    }),
    rejectProposal: builder.mutation({
      query: ({ id, reason }) => ({
        url: `/accounting/posting-proposals/${id}/reject/`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: [
        { type: "ReviewQueue", id: "LIST" },
        { type: "JournalApprovals", id: "LIST" },
        { type: "JournalApprovals", id: "SUMMARY" },
        { type: "AccountingDashboard", id: "SUMMARY" },
      ],
    }),
    pendingProposals: builder.query({
      query: () => `/accounting/posting-proposals/pending/`,
    }),
    generateMissingProposals: builder.mutation<
      {
        proposals_created: number;
        concepts_seeded: number;
        templates_seeded: number;
      },
      void
    >({
      query: () => ({
        url: `/accounting/posting-proposals/generate-missing/`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "ReviewQueue", id: "LIST" }],
    }),
  }),
});

// Timesheets
export const {
  useListTimesheetsQuery,
  useApproveTimesheetMutation,
  useCreateTimesheetMutation,
  useUpdateTimesheetEntryMutation,
} = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    listTimesheets: builder.query({
      query: () => `/accounting/timesheets/`,
      transformResponse: (r: any) => r?.results ?? r,
      providesTags: [{ type: "Timesheet", id: "LIST" }],
    }),
    approveTimesheet: builder.mutation({
      query: (id) => ({ url: `/accounting/timesheets/${id}/approve/`, method: "POST" }),
    }),
    createTimesheet: builder.mutation({
      query: (body) => ({
        url: `/accounting/timesheets/`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Timesheet", id: "LIST" }],
    }),
    updateTimesheetEntry: builder.mutation({
      query: ({
        id,
        program,
        assistance_program,
      }: {
        id: number;
        program?: number | null;
        assistance_program?: number | null;
      }) => ({
        url: `/accounting/timesheet-entries/${id}/`,
        method: "PATCH",
        body: { program, assistance_program },
      }),
      invalidatesTags: [{ type: "Timesheet", id: "LIST" }],
    }),
  }),
});

// Dashboard
export const { useAccountingDashboardQuery, useSetupStatusQuery, useTrialBalanceQuery } =
  accountingApi.injectEndpoints({
    endpoints: (builder) => ({
      accountingDashboard: builder.query({
        query: () => ({ url: `/accounting/accounting-dashboard/` }),
        providesTags: [{ type: "AccountingDashboard", id: "SUMMARY" }],
      }),
      setupStatus: builder.query({
        query: () => ({ url: `/accounting/setup-status/` }),
      }),
      trialBalance: builder.query({
        query: (params) => ({ url: `/accounting/reports/trial-balance/`, params }),
      }),
    }),
  });

// Exceptions
export const { useExceptionSummaryQuery, useDetectExceptionsMutation } =
  accountingApi.injectEndpoints({
    endpoints: (builder) => ({
      exceptionSummary: builder.query({
        query: () => ({ url: `/accounting/exceptions/summary/` }),
      }),
      detectExceptions: builder.mutation({
        query: () => ({ url: `/accounting/exceptions/detect/`, method: "POST" }),
      }),
    }),
  });

// Contribution Batches
export const { useListBatchesQuery, useCreateBatchMutation, useCloseBatchMutation } =
  accountingApi.injectEndpoints({
    endpoints: (builder) => ({
      listBatches: builder.query({
        query: () => `/accounting/contribution-batches/`,
        transformResponse: (r: any) => r?.results ?? r,
      }),
      createBatch: builder.mutation({
        query: (body) => ({ url: `/accounting/contribution-batches/`, method: "POST", body }),
      }),
      closeBatch: builder.mutation({
        query: (id) => ({
          url: `/accounting/contribution-batches/${id}/close/`,
          method: "POST",
        }),
      }),
    }),
  });

// ── v4.5.40: Additional endpoints for high-priority tabs ──

// Bank Reconciliations
export const { useListBankRecsQuery, useCompleteBankRecMutation } = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    listBankRecs: builder.query({
      query: () => `/accounting/bank-reconciliations/`,
      transformResponse: (r: any) => r?.results ?? r,
      providesTags: ["BankRec"],
    }),
    completeBankRec: builder.mutation({
      query: (id) => ({
        url: `/accounting/bank-reconciliations/${id}/complete/`,
        method: "POST",
      }),
    }),
  }),
});

// Month-End Closes
export const {
  useListMonthEndClosesQuery,
  useStartCloseMutation,
  useRunChecksMutation,
  useSignOffMutation,
  usePassTaskMutation,
} = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    listMonthEndCloses: builder.query({
      query: () => `/accounting/month-end-closes/`,
      transformResponse: (r: any) => r?.results ?? r,
    }),
    startClose: builder.mutation({
      query: (id) => ({
        url: `/accounting/month-end-closes/${id}/start/`,
        method: "POST",
      }),
    }),
    runChecks: builder.mutation({
      query: (id) => ({
        url: `/accounting/month-end-closes/${id}/run-checks/`,
        method: "POST",
      }),
    }),
    signOff: builder.mutation({
      query: (id) => ({
        url: `/accounting/month-end-closes/${id}/sign-off/`,
        method: "POST",
      }),
    }),
    passTask: builder.mutation({
      query: (id) => ({
        url: `/accounting/close-tasks/${id}/pass/`,
        method: "POST",
      }),
    }),
  }),
});

// Exceptions (full list)
export const { useListExceptionsQuery, useResolveExceptionMutation } =
  accountingApi.injectEndpoints({
    endpoints: (builder) => ({
      listExceptions: builder.query({
        query: (params) => ({ url: `/accounting/exceptions/`, params }),
        transformResponse: (r: any) => r?.results ?? r,
      }),
      resolveException: builder.mutation({
        query: ({ id, note }) => ({
          url: `/accounting/exceptions/${id}/resolve/`,
          method: "POST",
          body: { note },
        }),
      }),
    }),
  });

// Restriction Releases
export const {
  useListReleasesQuery,
  useCreateReleaseMutation,
  useConfirmReleaseMutation,
  useDetectReleasesMutation,
  useRejectReleaseMutation,
  useUploadReleaseAttachmentMutation,
} = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    listReleases: builder.query({
      query: () => `/accounting/restriction-releases/`,
      transformResponse: (r: any) => r?.results ?? r,
      providesTags: [{ type: "RestrictionRelease", id: "LIST" }],
    }),
    createRelease: builder.mutation({
      query: (body) => ({
        url: `/accounting/restriction-releases/`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "RestrictionRelease", id: "LIST" }],
    }),
    confirmRelease: builder.mutation({
      query: (id) => ({
        url: `/accounting/restriction-releases/${id}/confirm/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "ReviewQueue", id: "LIST" },
        { type: "RestrictionRelease", id: "LIST" },
      ],
    }),
    rejectRelease: builder.mutation({
      query: ({ id, reason }: { id: number; reason?: string }) => ({
        url: `/accounting/restriction-releases/${id}/reject/`,
        method: "POST",
        body: reason ? { reason } : {},
      }),
      invalidatesTags: [{ type: "RestrictionRelease", id: "LIST" }],
    }),
    detectReleases: builder.mutation({
      query: () => ({ url: `/accounting/restriction-releases/detect/`, method: "POST" }),
      invalidatesTags: [{ type: "RestrictionRelease", id: "LIST" }],
    }),
    uploadReleaseAttachment: builder.mutation({
      query: ({ id, file }: { id: number; file: File }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/accounting/restriction-releases/${id}/upload-attachment/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [{ type: "RestrictionRelease", id: "LIST" }],
    }),
  }),
});

// Credit Notes
export const { useListCreditNotesQuery, useApplyCreditNoteMutation, useCreateCreditNoteMutation } =
  accountingApi.injectEndpoints({
    endpoints: (builder) => ({
      listCreditNotes: builder.query({
        query: () => `/accounting/credit-notes/`,
        transformResponse: (r: any) => r?.results ?? r,
      }),
      applyCreditNote: builder.mutation({
        query: (id) => ({
          url: `/accounting/credit-notes/${id}/apply/`,
          method: "POST",
        }),
      }),
      createCreditNote: builder.mutation({
        query: (body) => ({
          url: `/accounting/credit-notes/`,
          method: "POST",
          body,
        }),
      }),
    }),
  });

// Review queue items
export const { useReviewQueueItemsQuery, useLinkQboBankTransactionMutation } =
  accountingApi.injectEndpoints({
    endpoints: (builder) => ({
      reviewQueueItems: builder.query<
        { count: number; results: unknown[] },
        {
          status?: string;
          type?: string;
          search?: string;
          date_from?: string;
          date_to?: string;
          ordering?: string;
          limit?: number;
          offset?: number;
        }
      >({
        query: (params) => ({
          url: `/accounting/review-queue/items/`,
          params,
        }),
        transformResponse: (r: { results?: unknown[]; count?: number }) => ({
          count: r?.count ?? 0,
          results: Array.isArray(r?.results) ? r.results : [],
        }),
        providesTags: [{ type: "ReviewQueue", id: "LIST" }],
      }),
      linkQboBankTransaction: builder.mutation({
        query: ({
          id,
          ...body
        }: {
          id: number;
          expense_id?: number;
          bill_id?: number;
          offset_account_id?: number;
          offset_side?: "debit" | "credit";
        }) => ({
          url: `/accounting/qbo-bank-transactions/${id}/link/`,
          method: "POST",
          body,
        }),
        invalidatesTags: [
          { type: "ReviewQueue", id: "LIST" },
          { type: "Bill", id: "LIST" },
          { type: "Bill", id: "AGING" },
          { type: "Bill", id: "PAYMENTS" },
          { type: "AccountingDashboard", id: "SUMMARY" },
        ],
      }),
    }),
  });

// Journal entry approvals (tier 2)
export const { useJournalApprovalsSummaryQuery, useJournalApprovalsItemsQuery } =
  accountingApi.injectEndpoints({
    endpoints: (builder) => ({
      journalApprovalsSummary: builder.query<{ count: number }, void>({
        query: () => ({ url: `/accounting/journal-approvals/` }),
        providesTags: [{ type: "JournalApprovals", id: "SUMMARY" }],
      }),
      journalApprovalsItems: builder.query({
        query: (params?: { limit?: number; offset?: number }) => ({
          url: `/accounting/journal-approvals/items/`,
          params,
        }),
        transformResponse: (r: { results?: unknown[]; count?: number }) => ({
          count: r?.count ?? 0,
          results: Array.isArray(r?.results) ? r.results : [],
        }),
        providesTags: [{ type: "JournalApprovals", id: "LIST" }],
      }),
    }),
  });

// Bills — create & submit
export const { useCreateBillMutation, useSubmitBillMutation } = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    createBill: builder.mutation({
      queryFn: async (
        body: {
          vendor: number;
          bill_number: string;
          bill_date: string;
          due_date: string;
          memo?: string;
          submission_payment_type?: string;
          payment_source?: string;
          payment_account?: number;
          amount_paid?: string;
          document?: File;
          tags?: { kind: string; id: number }[];
          lines: {
            description: string;
            amount: string;
            quantity?: string;
            program?: number;
            assistance_program?: number;
            fund?: number;
          }[];
        },
        _api,
        _extra,
        baseQuery,
      ) => {
        const { lines, document, tags, ...billData } = body;
        let billBody: Record<string, unknown> | FormData = billData;
        if (document) {
          const form = new FormData();
          for (const [key, value] of Object.entries(billData)) {
            if (value !== undefined && value !== null) {
              if (key === "tags" && Array.isArray(value)) {
                form.append("tags", JSON.stringify(value));
              } else {
                form.append(key, String(value));
              }
            }
          }
          form.append("document", document);
          billBody = form;
        } else if (tags && tags.length > 0) {
          billBody = { ...billData, tags };
        }
        const billResult = await baseQuery({
          url: `/accounting/bills/`,
          method: "POST",
          body: billBody,
        });
        if (billResult.error) return { error: billResult.error };
        const bill = billResult.data as { id: number };
        if (document && tags && tags.length > 0) {
          const tagResult = await baseQuery({
            url: `/accounting/bills/${bill.id}/`,
            method: "PATCH",
            body: { tags },
          });
          if (tagResult.error) return { error: tagResult.error };
        }
        for (const line of lines) {
          const lineResult = await baseQuery({
            url: `/accounting/bill-lines/`,
            method: "POST",
            body: { ...line, bill: bill.id },
          });
          if (lineResult.error) return { error: lineResult.error };
        }
        const submitResult = await baseQuery({
          url: `/accounting/bills/${bill.id}/submit/`,
          method: "POST",
        });
        if (submitResult.error) return { error: submitResult.error };
        return { data: bill };
      },
      invalidatesTags: [
        { type: "Bill", id: "LIST" },
        { type: "Bill", id: "AGING" },
        { type: "AccountingDashboard", id: "SUMMARY" },
        recentSubmissionsTag,
      ],
    }),
    submitBill: builder.mutation({
      query: (id: number) => ({
        url: `/accounting/bills/${id}/submit/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "Bill", id: "LIST" },
        { type: "Bill", id: "AGING" },
        { type: "AccountingDashboard", id: "SUMMARY" },
        recentSubmissionsTag,
      ],
    }),
  }),
});

// Invoices & customers
export const {
  useCreateInvoiceMutation,
  useListCustomersQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
} = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    createInvoice: builder.mutation({
      queryFn: async (
        body: {
          customer: number;
          issue_date: string;
          due_date: string;
          memo?: string;
          lines: { description: string; quantity: string; unit_price: string }[];
        },
        _api,
        _extra,
        baseQuery,
      ) => {
        const { lines, ...invoiceData } = body;
        const invoiceResult = await baseQuery({
          url: `/accounting/invoices/`,
          method: "POST",
          body: invoiceData,
        });
        if (invoiceResult.error) return { error: invoiceResult.error };
        const invoice = invoiceResult.data as { id: number };
        for (const line of lines) {
          const lineResult = await baseQuery({
            url: `/accounting/invoice-lines/`,
            method: "POST",
            body: { ...line, invoice: invoice.id },
          });
          if (lineResult.error) return { error: lineResult.error };
        }
        const submitResult = await baseQuery({
          url: `/accounting/invoices/${invoice.id}/submit/`,
          method: "POST",
        });
        if (submitResult.error) return { error: submitResult.error };
        return { data: invoice };
      },
      invalidatesTags: [recentSubmissionsTag],
    }),
    listCustomers: builder.query<unknown[], void>({
      query: () => `/accounting/customers/`,
      transformResponse: (r: { results?: unknown[] }) =>
        Array.isArray(r?.results) ? r.results : [],
    }),
    createCustomer: builder.mutation({
      query: (body) => ({
        url: `/accounting/customers/`,
        method: "POST",
        body,
      }),
    }),
    updateCustomer: builder.mutation({
      query: ({ id, ...body }: { id: number } & Record<string, unknown>) => ({
        url: `/accounting/customers/${id}/`,
        method: "PATCH",
        body,
      }),
    }),
  }),
});

// Vendors — create & update
export const {
  useCreateVendorMutation,
  useUpdateVendorMutation,
  useSaveVendorPaymentMutation,
  useGetVendorBankDetailsQuery,
  useConfirmVendorBankMutation,
} = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    createVendor: builder.mutation({
      query: (body) => ({
        url: `/accounting/vendors/`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Vendor", id: "LIST" }],
    }),
    updateVendor: builder.mutation({
      query: ({ id, ...body }: { id: number } & Record<string, unknown>) => ({
        url: `/accounting/vendors/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "Vendor", id: "LIST" }],
    }),
    saveVendorPayment: builder.mutation({
      query: ({ id, body }: { id: number; body: FormData }) => ({
        url: `/accounting/vendors/${id}/payment-details/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "Vendor", id: "LIST" }],
    }),
    getVendorBankDetails: builder.query({
      query: (id: number) => `/accounting/vendors/${id}/bank-details/`,
    }),
    confirmVendorBank: builder.mutation({
      query: ({ id, match }: { id: number; match: boolean }) => ({
        url: `/accounting/vendors/${id}/confirm-bank/`,
        method: "POST",
        body: { match },
      }),
      invalidatesTags: [{ type: "Vendor", id: "LIST" }],
    }),
  }),
});

// Payments
export const { useRecordPaymentMutation } = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    recordPayment: builder.mutation({
      queryFn: async (
        body: {
          customer: number;
          invoice?: number;
          amount: string;
          received_on: string;
          method?: string;
          reference?: string;
          notes?: string;
        },
        _api,
        _extra,
        baseQuery,
      ) => {
        const { invoice, ...paymentData } = body;
        const paymentResult = await baseQuery({
          url: `/accounting/payments/`,
          method: "POST",
          body: paymentData,
        });
        if (paymentResult.error) return { error: paymentResult.error };
        const payment = paymentResult.data as { id: number };
        if (invoice) {
          const appResult = await baseQuery({
            url: `/accounting/payment-applications/`,
            method: "POST",
            body: {
              payment: payment.id,
              invoice,
              amount: body.amount,
            },
          });
          if (appResult.error) return { error: appResult.error };
        }
        return { data: payment };
      },
    }),
  }),
});

// Payments — AR list + customer statements; AP bill payments
export const { useListPaymentsQuery, useListBillPaymentsQuery, useCustomerStatementQuery } =
  accountingApi.injectEndpoints({
    endpoints: (builder) => ({
      listPayments: builder.query({
        query: () => `/accounting/payments/`,
        transformResponse: (r: any) => r?.results ?? r,
      }),
      listBillPayments: builder.query({
        query: () => `/accounting/bill-payments/`,
        transformResponse: (r: any) => r?.results ?? r,
        providesTags: [{ type: "Bill", id: "PAYMENTS" }],
      }),
      customerStatement: builder.query({
        query: ({ customerId, from, to }: { customerId: number; from?: string; to?: string }) => ({
          url: `/accounting/customers/${customerId}/statement/`,
          params: { from, to },
        }),
      }),
    }),
  });

// Budgets — create, submit, approve (activate)
export const { useCreateBudgetMutation, useSubmitBudgetMutation, useApproveBudgetMutation } =
  accountingApi.injectEndpoints({
    endpoints: (builder) => ({
      createBudget: builder.mutation({
        query: (body) => ({
          url: `/accounting/budgets/`,
          method: "POST",
          body,
        }),
      }),
      submitBudget: builder.mutation({
        query: (id: number) => ({
          url: `/accounting/budgets/${id}/submit/`,
          method: "POST",
        }),
      }),
      approveBudget: builder.mutation({
        query: (id: number) => ({
          url: `/accounting/budgets/${id}/activate/`,
          method: "POST",
        }),
      }),
    }),
  });

type SyncPage = { results?: unknown[]; next?: string | null };

/** Walk every page. The default list is 10 rows, which hid older failures. */
async function fetchEverySyncPage(
  baseQuery: (arg: {
    url: string;
    params?: Record<string, string | number>;
  }) => Promise<{ data?: SyncPage | unknown[]; error?: { status: number; data: unknown } }>,
  url: string,
  params?: { status?: string },
) {
  const pageSize = 100;
  const rows: unknown[] = [];
  let page = 1;
  for (;;) {
    const result = await baseQuery({
      url,
      params: {
        ...(params?.status ? { status: params.status } : {}),
        page,
        page_size: pageSize,
      },
    });
    if (result.error) return { error: result.error };
    const body = result.data;
    if (Array.isArray(body)) return { data: body };
    const chunk = Array.isArray(body?.results) ? body.results : [];
    rows.push(...chunk);
    if (!body?.next || chunk.length === 0) return { data: rows };
    page += 1;
    if (page > 100) return { data: rows };
  }
}

// Bill.com integration
export const {
  useBillcomStatusQuery,
  useBillcomConnectMutation,
  useBillcomDisconnectMutation,
  useListBillcomSyncQuery,
  useRetryBillcomSyncMutation,
} = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    billcomStatus: builder.query({
      query: () => ({ url: `/accounting/billcom-connections/status/` }),
      providesTags: [{ type: "BillCom", id: "STATUS" }],
    }),
    billcomConnect: builder.mutation({
      query: () => ({
        url: `/accounting/billcom-connections/connect/`,
        method: "POST",
      }),
    }),
    billcomDisconnect: builder.mutation({
      query: () => ({
        url: `/accounting/billcom-connections/disconnect/`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "BillCom", id: "STATUS" }],
    }),
    listBillcomSync: builder.query<unknown[], { status?: string } | void>({
      async queryFn(params, _api, _extra, baseQuery) {
        return fetchEverySyncPage(
          baseQuery as Parameters<typeof fetchEverySyncPage>[0],
          "/accounting/billcom-sync/",
          params || undefined,
        );
      },
      providesTags: [{ type: "BillCom", id: "SYNC_LIST" }],
    }),
    retryBillcomSync: builder.mutation({
      query: (id: number) => ({
        url: `/accounting/billcom-sync/${id}/retry/`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "BillCom", id: "SYNC_LIST" }],
    }),
  }),
});

// QBO sync records
export const {
  useListQboSyncQuery,
  useRetryQboSyncMutation,
  useQboReconcileMutation,
  useQboImportOpeningBalancesMutation,
} = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    listQboSync: builder.query<unknown[], { status?: string } | void>({
      async queryFn(params, _api, _extra, baseQuery) {
        return fetchEverySyncPage(
          baseQuery as Parameters<typeof fetchEverySyncPage>[0],
          "/accounting/qbo-sync/",
          params || undefined,
        );
      },
      providesTags: [{ type: "QBO", id: "SYNC_LIST" }],
    }),
    retryQboSync: builder.mutation({
      query: (id: number) => ({
        url: `/accounting/qbo-sync/${id}/retry/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "QBO", id: "SYNC_LIST" },
        { type: "QBO", id: "PENDING_JOURNALS" },
        { type: "Journal", id: "LIST" },
      ],
    }),
    qboReconcile: builder.mutation<
      {
        ok: boolean;
        error?: string;
        rows?: {
          account_id: number;
          account: string;
          name: string;
          tnn_balance: string;
          qbo_balance: string;
          difference: string;
        }[];
        drift_count?: number;
      },
      void
    >({
      query: () => ({
        url: "/accounting/qbo-sync/reconcile/",
        method: "POST",
      }),
    }),
    qboImportOpeningBalances: builder.mutation<
      { ok: boolean; posted?: boolean; message?: string; journal_id?: number },
      void
    >({
      query: () => ({
        url: "/accounting/qbo-sync/import-opening-balances/",
        method: "POST",
      }),
    }),
  }),
});

// Bank transfers (operating ↔ savings — asset to asset, not revenue/expense)
export const {
  useListBankTransfersQuery,
  useCreateBankTransferMutation,
  useOperationalApproveBankTransferMutation,
  useConfirmBankTransferMutation,
  useRejectBankTransferMutation,
  useUploadBankTransferAttachmentMutation,
} = accountingApi.injectEndpoints({
  endpoints: (builder) => ({
    listBankTransfers: builder.query({
      query: () => `/accounting/bank-transfers/`,
      transformResponse: (r: { results?: unknown[] }) =>
        Array.isArray(r?.results) ? r.results : [],
      providesTags: [{ type: "BankTransfer", id: "LIST" }],
    }),
    createBankTransfer: builder.mutation({
      query: (body) => ({
        url: `/accounting/bank-transfers/`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "BankTransfer", id: "LIST" }],
    }),
    operationalApproveBankTransfer: builder.mutation({
      query: (id: number) => ({
        url: `/accounting/bank-transfers/${id}/operational-approve/`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "BankTransfer", id: "LIST" }],
    }),
    confirmBankTransfer: builder.mutation({
      query: (id: number) => ({
        url: `/accounting/bank-transfers/${id}/confirm/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "BankTransfer", id: "LIST" },
        { type: "ReviewQueue", id: "LIST" },
      ],
    }),
    rejectBankTransfer: builder.mutation({
      query: ({ id, reason }: { id: number; reason?: string }) => ({
        url: `/accounting/bank-transfers/${id}/reject/`,
        method: "POST",
        body: reason ? { reason } : {},
      }),
      invalidatesTags: [{ type: "BankTransfer", id: "LIST" }],
    }),
    uploadBankTransferAttachment: builder.mutation({
      query: ({ id, file }: { id: number; file: File }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/accounting/bank-transfers/${id}/upload-attachment/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [{ type: "BankTransfer", id: "LIST" }],
    }),
  }),
});
